import React from 'react';
import reframe from 'nike-re-framejs';
import Immutable from 'immutable';
import './LiveScoreboardNg.less';
import { MatchMediaProvider } from 'nike-ui/src/components/turboLive/hooks/useMatchMedia';
import TurboLiveProvider from '@/turbo-live/context/TurboLiveProvider';
import { MatchTimer } from 'nike-ui/src/components/turboLive/components/MatchTimer';
import LiveMatchFacade from '../../services/liveMatchFacade';
import { SvgIcon } from 'nike-ui/src/icons/SvgIcon';
import i18n from '@i18n';
import clsx from 'clsx';
import { getClassNameByBet } from 'nike-ui/src/common/utils/utils';
import { localizeMap } from 'nike-ui/src/components/turboLive/utils';
import { MatchScoreboard } from 'nike-ui/src/components/turboLive/components/Match';
import { isRainDelayExtra, getScoreboardType } from 'nike-ui/src/components/turboLive/matchExtras';
import { useScoreboardTypes } from 'nike-ui/src/components/turboLive/hooks/useScoreboardTypes';

export function TicketScoreboardOpponents({ home, away, server, stats }) {
    const localizedHome = localizeMap(home);
    const localizedAway = localizeMap(away);
    const penalties = stats?.get('SUSPENDS');
    const homePenalties = parseInt(penalties?.split(':')[0]);
    const awayPenalties = parseInt(penalties?.split(':')[1]);
    const cssClass = 'ellipsis f-condensed text-extra-bold c-black-100';

    return (
        <div className="flex flex-col justify-center fs-12 lh-1-5 mr-20">
            <div className="flex items-end">
                {server === 'HOME' ? (
                    <span className="ticket-match-serve icon-space" />
                ) : server === '' ? (
                    <span className="dummy-space" />
                ) : null}
                <div className={cssClass}>{localizedHome}</div>
                {homePenalties > 0 && (
                    <span
                        className={clsx({
                            'flex-center fs-10 c-white ml-3 mb-3 br-radius-circle bg-red icon-space':
                                penalties && homePenalties
                        })}
                    >
                        {homePenalties}
                    </span>
                )}
            </div>
            <div className="flex items-end">
                {server === 'AWAY' ? (
                    <span className={'ticket-match-serve icon-space'} />
                ) : server === 'HOME' ? (
                    <span className="dummy-space" />
                ) : null}
                <div className={cssClass}>{localizedAway}</div>
                {awayPenalties > 0 && (
                    <span
                        className={clsx({
                            'flex-center fs-10 c-white ml-3 mb-3 br-radius-circle bg-red icon-space':
                                penalties && awayPenalties
                        })}
                    >
                        {awayPenalties}
                    </span>
                )}
            </div>
        </div>
    );
}

function getHomeAndAwayStats(stats) {
    const parsedStats = stats?.split(':');
    return [parsedStats[0], parsedStats[1]];
}

function getStatsIcon(name) {
    switch (name) {
        case 'RED_CARDS':
            return <div className="ticket-red-card-icon mb-5" />;
        case 'YELLOW_CARDS':
            return <div className="ticket-yellow-card-icon mb-5" />;
        case 'CORNERS':
            return <SvgIcon imgId={'corner-kick'} className="mb-5" sizeX={'12px'} sizeY={'12px'} />;
        default:
            return '';
    }
}

function StatsData({ statistics, name }) {
    const statsCssClass = 'flex items-end justify-center text-right flex-col match-score-col fs-12 c-default-light';
    const [home, away] = getHomeAndAwayStats(statistics);
    const icon = getStatsIcon(name);
    return icon ? (
        <div className={statsCssClass}>
            {icon}
            <span>{home}</span>
            <span>{away}</span>
        </div>
    ) : null;
}

const StatsOrder = {
    CORNERS: 0,
    YELLOW_CARDS: 1,
    RED_CARDS: 2
};

export function MatchStats({ stats }) {
    if (!stats) {
        return null;
    }
    return (
        <div className={'flex items-end lh-1-5'}>
            {stats
                .entrySeq()
                .sortBy(([name]) => StatsOrder[name])
                .map(([name, statistics]) => (
                    <StatsData statistics={statistics} name={name} key={name} />
                ))}
        </div>
    );
}

export function LiveTicketScoreboardNg({ subsPrefix, eventId, bet }) {
    const derefSub = reframe.useReframe();
    const event = derefSub([subsPrefix + '.event', eventId]) || Immutable.Map();

    useScoreboardTypes();

    if (!event) {
        return null;
    }

    const match = event.get('match');
    const score = match.get('score');
    const sportId = event.get('sportId');
    const isRainDelay = isRainDelayExtra(match);
    const isLiveBet = match.get('isLive');

    return (
        <div
            className={
                'live-sport-scoreboard-ng bg-default sb-' +
                LiveMatchFacade.sportType(sportId) +
                ' scoreboard-bg ' +
                getClassNameByBet(bet)
            }
        >
            <div className={'sb-gradient-wrap'}>
                <TurboLiveProvider>
                    <MatchMediaProvider>
                        <div className="flex">
                            <div className="flex-1 flex flex-col">
                                <div className="flex mb-2">
                                    {isLiveBet && <div className="event-flag Live mr-5">{i18n('event.flag.live')}</div>}
                                    <MatchTimer
                                        scoreboardTypeId={getScoreboardType(match)}
                                        matchTimer={match.get('timer')}
                                        otherClass="fs-12 c-black-50"
                                    />
                                    {isRainDelay && (
                                        <SvgIcon imgId="real-cloud" sizeX="14" sizeY="14" className="ml-10 c-red" />
                                    )}
                                </div>
                                <TicketScoreboardOpponents
                                    home={match.get('home')}
                                    away={match.get('away')}
                                    server={score.get('server')}
                                    stats={match.get('stats')}
                                />
                            </div>
                            <MatchScoreboard score={score} sportId={sportId} ticketScoreboard />
                            <MatchStats stats={match.get('stats')} />
                        </div>
                    </MatchMediaProvider>
                </TurboLiveProvider>
            </div>
        </div>
    );
}
